import {
  Component,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DistrictDTO } from 'src/app/common/dtos/district.dto';
import { HideSessionTypeModalComponent } from '../../shared/components/hide-session-type-modal/hide-session-type-modal.component';
import { CreateEditSessionTypeModalComponent } from '../../shared/components/modals/create-edit-session-type-modal/create-edit-session-type-modal.component';
import { SessionTypePreviewModalComponent } from '../../shared/components/session-type-preview-modal/session-type-preview-modal.component';
import {
  CoachingLogType,
  CoachingSessionTypeDTO,
} from '../../shared/dtos/coaching-session.dto';
import {
  SortEvent,
  SortableHeader,
} from '../../shared/directives/sortable-header.directive';
import {
  getSessionTypeLayoutLabel,
  getSessionTypeLayoutThumbnail,
} from '../../shared/helpers/session-type/session-type-layout.constants';
import { DistrictListService } from '../../shared/services/district-list/district-list-service';
import { SessionTypeService } from '../../shared/services/session-type/session-type.service';

interface LayoutFilterOption {
  label: string;
  views: CoachingLogType[];
}

const LAYOUT_FILTER_OPTIONS: LayoutFilterOption[] = [
  { label: 'General Session', views: ['general-log'] },
  {
    label: 'Coaching Conversation',
    views: ['universal-log', 'egrowe-coachlog-v2'],
  },
  { label: 'Observation and Feedback', views: ['observation'] },
];

const columnDataMap = (
  row: CoachingSessionTypeDTO,
  column: string
): string | number => {
  switch (column) {
    case 'layout':
      return getSessionTypeLayoutLabel(row);
    case 'activityCount':
      return row.activityCount ?? -1;
    case 'title':
    default:
      return row.title || '';
  }
};

const compare = (v1: string | number, v2: string | number): number => {
  if (typeof v1 === 'string' && typeof v2 === 'string') {
    return v1.toUpperCase().localeCompare(v2.toUpperCase());
  }
  // eslint-disable-next-line no-nested-ternary
  return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
};

@Component({
  selector: 'app-session-types-page',
  templateUrl: './session-types-page.component.html',
  styleUrl: './session-types-page.component.scss',
  standalone: false,
})
export class SessionTypesPageComponent implements OnInit {
  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;

  @ViewChild('hideSessionTypeModal')
  hideSessionTypeModal: HideSessionTypeModalComponent;

  @ViewChild('createEditSessionTypeModal')
  createEditSessionTypeModal: CreateEditSessionTypeModalComponent;

  @ViewChild('sessionTypePreviewModal')
  sessionTypePreviewModal: SessionTypePreviewModalComponent;

  getLayoutLabel = getSessionTypeLayoutLabel;

  getLayoutThumbnail = getSessionTypeLayoutThumbnail;

  layoutFilterOptions = LAYOUT_FILTER_OPTIONS;

  layoutFilter = '';

  districtCode: string;

  district: DistrictDTO;

  isLoading = true;

  sessionTypes: CoachingSessionTypeDTO[];

  activeTypes: CoachingSessionTypeDTO[] = [];

  hiddenTypes: CoachingSessionTypeDTO[] = [];

  searchTerm = '';

  sortInfo = {
    column: '',
    direction: '',
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortIconTemp: any = {
    title: 'gray',
    layout: 'gray',
    activityCount: 'gray',
  };

  constructor(
    private route: ActivatedRoute,
    private districtListService: DistrictListService,
    private sessionTypeService: SessionTypeService
  ) {}

  ngOnInit(): void {
    this.districtCode = this.route.snapshot.paramMap.get(
      'districtCode'
    ) as string;
    this.districtListService
      .fetchDistrictsSimple({ per_page: 1000 })
      .subscribe((districts) => {
        if (districts) {
          const district = districts.find(
            (searchDistrict) =>
              searchDistrict.districtCode === this.districtCode
          ) as DistrictDTO;
          if (district) {
            this.district = district;
            this.getSessionTypes();
          }
        }
      });
  }

  getSessionTypes(): void {
    this.isLoading = true;
    this.sessionTypeService
      .getSessionTypes(this.district.id)
      .subscribe((sessionTypes) => {
        this.sessionTypes = sessionTypes;
        this.applyFilterAndSort();
        this.isLoading = false;
      });
  }

  searchTermChanged(event: Event): void {
    this.searchTerm = (<HTMLInputElement>event.target).value;
    this.applyFilterAndSort();
  }

  layoutFilterChanged(event: Event): void {
    this.layoutFilter = (<HTMLSelectElement>event.target).value;
    this.applyFilterAndSort();
  }

  onSort({ column, direction }: SortEvent): void {
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
        this.sortIconTemp[header.sortable] = 'gray';
      } else {
        this.sortIconTemp[header.sortable] = 'none';
      }
    });

    this.sortInfo = { column, direction };
    if (direction === '' || column === '') {
      this.sortIconTemp[column] = 'gray';
    }
    this.applyFilterAndSort();
  }

  isOwnedByCurrentDistrict(sessionType: CoachingSessionTypeDTO): boolean {
    return sessionType.districtId === this.district.id;
  }

  openCreateModal(): void {
    this.createEditSessionTypeModal.openCreate();
  }

  openEditModal(sessionType: CoachingSessionTypeDTO): void {
    this.createEditSessionTypeModal.openEdit(sessionType);
  }

  openHideModal(sessionType: CoachingSessionTypeDTO): void {
    this.hideSessionTypeModal.open(sessionType);
  }

  openPreviewModal(sessionType: CoachingSessionTypeDTO): void {
    this.sessionTypePreviewModal.open(sessionType);
  }

  onConfirmHide(sessionType: CoachingSessionTypeDTO): void {
    this.sessionTypeService
      .setSessionTypeHidden(sessionType.id, true, this.district.id)
      .subscribe(() => {
        this.getSessionTypes();
      });
  }

  onRestore(sessionType: CoachingSessionTypeDTO): void {
    this.sessionTypeService
      .setSessionTypeHidden(sessionType.id, false, this.district.id)
      .subscribe(() => {
        this.getSessionTypes();
      });
  }

  private applyFilterAndSort(): void {
    if (!this.sessionTypes) {
      return;
    }

    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.sessionTypes;
    if (term) {
      filtered = filtered.filter(
        (sessionType) =>
          sessionType.title.toLowerCase().includes(term) ||
          (sessionType.description || '').toLowerCase().includes(term)
      );
    }

    if (this.layoutFilter) {
      const selectedOption = this.layoutFilterOptions.find(
        (option) => option.label === this.layoutFilter
      );
      if (selectedOption) {
        filtered = filtered.filter((sessionType) =>
          selectedOption.views.includes(sessionType.view)
        );
      }
    }

    const { column, direction } = this.sortInfo;
    const sortFn = (
      a: CoachingSessionTypeDTO,
      b: CoachingSessionTypeDTO
    ): number => {
      if (!column || !direction) {
        return 0;
      }
      const res = compare(columnDataMap(a, column), columnDataMap(b, column));
      return direction === 'asc' ? res : -res;
    };

    this.activeTypes = filtered.filter((t) => !t.isHidden).sort(sortFn);
    this.hiddenTypes = filtered.filter((t) => !!t.isHidden).sort(sortFn);
  }
}
